import { useMemo, useState, useEffect } from 'react';
import { Plus, RotateCcw, X } from 'lucide-react';

import { ConfigEditorModal, ConfigSection, ConfigSummaryTile } from './config-editor-modal';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  useFrameworkDefaultsQuery,
  useSetFrameworkDefaultsMutation,
  WidgetTypeSchema,
  type WidgetDefaults,
  type WidgetType,
  type WidgetBinding,
} from '@/lib/api/framework-defaults';

/**
 * T-0114 / E01-F09 — Widget defaults editor.
 *
 * Visual contract:
 *   - `design_handoff_caliber_v2/prototype/modals-round4b.js → openWidgetEditor`
 *   - README §4 modal table row `data-open-widgets`
 *
 * Behavior:
 *   - Loads the live `widget_defaults` blob for a framework definition
 *     via `useFrameworkDefaultsQuery`. The hook returns both the stored
 *     blob (or null) and the engine-inferred defaults; the editor seeds
 *     from `stored ?? inferred_defaults` so a first-time configuration
 *     starts with a sensible baseline.
 *   - The Lead can edit the main view binding, reorder the top-row
 *     bindings, and tweak the default filter. Each row binds a field
 *     name to a widget type from the v1 palette
 *     (`WidgetTypeSchema` mirrors the backend palette).
 *   - Save replaces the blob via PUT (last-write-wins per spec §9).
 *   - "Reset to inferred" reverts the buffered state to the backend's
 *     inferred defaults — useful when starting over.
 *
 * Trigger arg semantics:
 *   - The prototype passes the methodology template id to
 *     `openWidgetEditor`. Production passes the `frameworkDefinitionId`
 *     resolved by the FrameworkCard. The shell wires the modal with
 *     whatever `data-open-widgets="<value>"` carries; the value must be
 *     the FrameworkDefinition id. (Component callers already do this —
 *     see `apps/web/src/components/settings/framework-card.tsx`.)
 */

export interface WidgetEditorModalProps {
  frameworkDefinitionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const WIDGET_TYPES: WidgetType[] = WidgetTypeSchema.options;

function cloneDefaults(d: WidgetDefaults): WidgetDefaults {
  return {
    top_row: d.top_row.map((b) => ({ ...b })),
    main_view: { ...d.main_view },
    default_filter: d.default_filter ? { ...d.default_filter } : undefined,
  };
}

export function WidgetEditorModal({
  frameworkDefinitionId,
  open,
  onOpenChange,
}: WidgetEditorModalProps) {
  const query = useFrameworkDefaultsQuery(open ? frameworkDefinitionId : undefined);
  const baseline = useMemo<WidgetDefaults | null>(() => {
    if (!query.data) return null;
    return query.data.stored ?? query.data.inferred_defaults;
  }, [query.data]);

  const [draft, setDraft] = useState<WidgetDefaults | null>(null);

  useEffect(() => {
    if (baseline) {
      setDraft(cloneDefaults(baseline));
    }
  }, [baseline]);

  const isDirty = useMemo(() => {
    if (!baseline || !draft) return false;
    return JSON.stringify(baseline) !== JSON.stringify(draft);
  }, [baseline, draft]);

  const mutation = useSetFrameworkDefaultsMutation(frameworkDefinitionId);
  const [saveError, setSaveError] = useState<unknown>(null);

  const handleSave = async ({ closeOnSuccess }: { closeOnSuccess: boolean }) => {
    if (!draft) return;
    setSaveError(null);
    try {
      await mutation.mutateAsync(draft);
      if (closeOnSuccess) onOpenChange(false);
    } catch (err) {
      setSaveError(err);
    }
  };

  return (
    <ConfigEditorModal
      open={open}
      onOpenChange={onOpenChange}
      bread="Firm settings · Methodology library"
      title="Widget defaults editor"
      sub="Configure how this register framework renders in every engagement. The schema-driven dynamic dashboard renderer applies these defaults; unset values fall back to field-type inference. Engagements may override at setup."
      isDirty={isDirty}
      onSave={handleSave}
      saving={mutation.isPending}
      saveError={saveError ? <ErrorMessage error={saveError} /> : undefined}
      foot="Changes to firm-level defaults propagate to new engagements. Existing engagements keep their pinned methodology version."
      summary={
        query.isLoading ? (
          <div className="col-span-full flex items-center gap-2 text-body-sm text-ink-500">
            <Spinner /> Loading framework defaults…
          </div>
        ) : draft ? (
          <>
            <ConfigSummaryTile
              label="Top-row widgets"
              value={draft.top_row.length}
              sub="rendered above the main view"
            />
            <ConfigSummaryTile
              label="Main field"
              value={draft.main_view.field || '—'}
              sub={draft.main_view.widget}
            />
            <ConfigSummaryTile
              label="Default filter"
              value={draft.default_filter ? draft.default_filter.field : 'none'}
              sub={
                draft.default_filter
                  ? `${draft.default_filter.field} = ${String(draft.default_filter.value)}`
                  : 'no filter applied on load'
              }
            />
            <ConfigSummaryTile
              label="Source"
              value={query.data?.stored ? 'Configured' : 'Inferred'}
              sub={
                query.data?.stored
                  ? 'stored override active'
                  : 'falling back to field-type inference'
              }
            />
          </>
        ) : null
      }
    >
      {query.isLoading ? (
        <div className="rounded-card border border-ink-100 bg-paper-0 p-card-pad text-body-sm text-ink-500">
          Loading…
        </div>
      ) : query.error ? (
        <ErrorMessage error={query.error} />
      ) : draft ? (
        <WidgetBody draft={draft} setDraft={setDraft} inferred={query.data?.inferred_defaults} />
      ) : null}
    </ConfigEditorModal>
  );
}

function WidgetBody({
  draft,
  setDraft,
  inferred,
}: {
  draft: WidgetDefaults;
  setDraft: (next: WidgetDefaults) => void;
  inferred: WidgetDefaults | undefined;
}) {
  const updateMain = (updates: Partial<WidgetBinding>) => {
    setDraft({ ...draft, main_view: { ...draft.main_view, ...updates } });
  };
  const updateTopRow = (i: number, updates: Partial<WidgetBinding>) => {
    setDraft({
      ...draft,
      top_row: draft.top_row.map((b, idx) => (idx === i ? { ...b, ...updates } : b)),
    });
  };
  const removeTopRow = (i: number) => {
    setDraft({ ...draft, top_row: draft.top_row.filter((_, idx) => idx !== i) });
  };
  const addTopRow = () => {
    setDraft({
      ...draft,
      top_row: [...draft.top_row, { field: '', widget: 'string_chip' }],
    });
  };

  const resetToInferred = () => {
    if (inferred) setDraft(cloneDefaults(inferred));
  };

  return (
    <>
      <ConfigSection
        heading="Main view"
        meta="The default field surfaced in detail panels and full-row drawers."
      >
        <div className="grid gap-3 px-4 py-3 sm:grid-cols-[1fr,200px]">
          <div>
            <label className="block text-meta text-ink-500">Field name</label>
            <input
              type="text"
              value={draft.main_view.field}
              onChange={(e) => updateMain({ field: e.target.value })}
              placeholder="e.g. title"
              className="mt-1 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
            />
          </div>
          <div>
            <label className="block text-meta text-ink-500">Widget</label>
            <select
              value={draft.main_view.widget}
              onChange={(e) => updateMain({ widget: e.target.value as WidgetType })}
              className="mt-1 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-700 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
            >
              {WIDGET_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>
      </ConfigSection>

      <ConfigSection
        heading="Top-row widgets"
        meta="Rendered above the main view in registration order. Drag to reorder (future)."
        actions={
          <button
            type="button"
            onClick={addTopRow}
            className="inline-flex items-center gap-1 rounded-card border border-ink-200 px-2 py-1 text-meta text-ink-700 hover:bg-paper-100"
          >
            <Plus className="h-3 w-3" aria-hidden="true" />
            Add widget
          </button>
        }
      >
        {draft.top_row.length === 0 ? (
          <div className="px-4 py-4 text-body-sm text-ink-500">
            No top-row widgets. The dashboard will fall back to field-type inference until you add
            at least one.
          </div>
        ) : (
          draft.top_row.map((b, i) => (
            <div
              key={`${b.field}-${i}`}
              className="grid gap-3 px-4 py-3 sm:grid-cols-[16px,1fr,200px,auto]"
            >
              <div className="pt-2 text-ink-300" aria-hidden="true">
                ≡
              </div>
              <div>
                <label className="block text-meta text-ink-500">Field name</label>
                <input
                  type="text"
                  value={b.field}
                  onChange={(e) => updateTopRow(i, { field: e.target.value })}
                  placeholder="field"
                  className="mt-1 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
                />
              </div>
              <div>
                <label className="block text-meta text-ink-500">Widget</label>
                <select
                  value={b.widget}
                  onChange={(e) => updateTopRow(i, { widget: e.target.value as WidgetType })}
                  className="mt-1 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-700 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
                >
                  {WIDGET_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => removeTopRow(i)}
                  title="Remove widget"
                  className="inline-flex h-input-h items-center justify-center rounded-card border border-ink-200 px-2 text-posture-critical hover:bg-posture-critical-tint"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              </div>
            </div>
          ))
        )}
      </ConfigSection>

      <ConfigSection
        heading="Default filter"
        meta="Applied when the register first loads; user-clearable per session."
      >
        <div className="grid gap-3 px-4 py-3 sm:grid-cols-[1fr,1fr,auto]">
          <div>
            <label className="block text-meta text-ink-500">Field</label>
            <input
              type="text"
              value={draft.default_filter?.field ?? ''}
              onChange={(e) => {
                const field = e.target.value;
                if (field.length === 0) {
                  const { default_filter, ...rest } = draft;
                  setDraft(rest);
                  return;
                }
                setDraft({
                  ...draft,
                  default_filter: {
                    field,
                    value: draft.default_filter?.value ?? '',
                  },
                });
              }}
              placeholder="leave blank for no filter"
              className="mt-1 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
            />
          </div>
          <div>
            <label className="block text-meta text-ink-500">Value</label>
            <input
              type="text"
              value={String(draft.default_filter?.value ?? '')}
              disabled={!draft.default_filter}
              onChange={(e) => {
                if (!draft.default_filter) return;
                setDraft({
                  ...draft,
                  default_filter: {
                    field: draft.default_filter.field,
                    value: e.target.value,
                  },
                });
              }}
              className="mt-1 block h-input-h w-full rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-amber-600 disabled:cursor-not-allowed disabled:bg-paper-100"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={resetToInferred}
              disabled={!inferred}
              title="Reset all bindings to the engine-inferred defaults"
              className="inline-flex h-input-h items-center gap-1 rounded-card border border-ink-200 px-2 text-meta text-ink-700 hover:bg-paper-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RotateCcw className="h-3 w-3" aria-hidden="true" />
              Reset to inferred
            </button>
          </div>
        </div>
      </ConfigSection>

      <ConfigSection heading="Inference fallback" meta="For frameworks without explicit defaults">
        <div className="px-4 py-3 text-meta text-ink-700">
          When a framework lacks explicit defaults (e.g. a newly promoted AI-derived custom
          framework), the renderer infers from field types: <code>string</code> → text / string_chip
          · <code>enum</code> → enum_chip · <code>date</code> → date · <code>uuid</code> →
          uuid_link. Firm Admin can tune later.
        </div>
      </ConfigSection>
    </>
  );
}
